import React from 'react';
import { Pencil, Trash2, UserCheck, X } from 'lucide-react';
import { Requester } from '../../../models/RequesterModel';
import DataTable, { DataTableColumn } from '../../../components/Base/DataTable';
import StatusBadge from '../../../components/Base/StatusBadge';
import CompanyCell from '../../../components/Base/CompanyCell';

type Props = {
  data: Requester[];
  loading?: boolean;
  onEdit: (requester: Requester) => void;
  onDelete: (id: number) => void;
  toolbar?: React.ReactNode;
  footer?: React.ReactNode;
  totalCount?: number;
  searchTerm?: string;
  onClearSearch?: () => void;
};

function formatCreatedParts(value?: string | null): { date: string; time: string } | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return {
    date: d.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }),
    time: d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
}

function fullName(row: Requester): string {
  return [row.name, row.first_lastname, row.second_lastname]
    .map((p) => (p ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

const RequestersTable: React.FC<Props> = ({
  data,
  loading,
  onEdit,
  onDelete,
  toolbar,
  footer,
  totalCount,
  searchTerm = '',
  onClearSearch,
}) => {
  const term = searchTerm.trim();
  const shown = data.length;
  const total = totalCount ?? shown;

  const columns: DataTableColumn<Requester>[] = [
    {
      id: 'id',
      header: 'ID',
      width: '96px',
      label: 'ID',
      cell: (row) => <span className="data-table__id">{row.id ?? '—'}</span>,
    },
    {
      id: 'company_name',
      header: 'Empresa',
      label: 'Empresa',
      cell: (row) => <CompanyCell name={row.company_name} />,
    },
    {
      id: 'name',
      header: 'Solicitante',
      label: 'Solicitante',
      cell: (row) => {
        const name = fullName(row) || '—';
        return (
          <div className="data-table__entity">
            <span className="data-table__entity-avatar" aria-hidden>
              <UserCheck size={16} strokeWidth={2.25} />
            </span>
            <span className="data-table__entity-text">
              <span className="data-table__entity-name" title={name}>
                {name}
              </span>
              {row.id != null ? (
                <span className="data-table__entity-sub">Ref. #{row.id}</span>
              ) : null}
            </span>
          </div>
        );
      },
    },
    {
      id: 'phone_number',
      header: 'Teléfono',
      label: 'Teléfono',
      width: '140px',
      cell: (row) => (
        <span className="data-table__muted">{row.phone_number?.trim() || '—'}</span>
      ),
    },
    {
      id: 'email',
      header: 'Correo',
      label: 'Correo',
      cell: (row) => (
        <span className="data-table__muted" title={row.email || undefined}>
          {row.email?.trim() || '—'}
        </span>
      ),
    },
    {
      id: 'created_at',
      header: 'Creado',
      label: 'Creado',
      width: '148px',
      cell: (row) => {
        const parts = formatCreatedParts(row.created_at);
        if (!parts) return <span className="data-table__muted">—</span>;
        return (
          <span className="data-table__datetime">
            <span className="data-table__date">{parts.date}</span>
            <span className="data-table__time">{parts.time}</span>
          </span>
        );
      },
    },
    {
      id: 'is_active',
      header: 'Estado',
      label: 'Estado',
      width: '120px',
      cell: (row) => (
        <StatusBadge
          label={row.is_active ? 'Activo' : 'Inactivo'}
          tone={row.is_active ? 'success' : 'neutral'}
        />
      ),
    },
  ];

  const summary = !loading ? (
    <>
      <span className="data-table__summary-label">
        {term
          ? `${shown} resultado${shown === 1 ? '' : 's'} en esta página`
          : `Mostrando ${shown} de ${total} solicitantes`}
      </span>
      {term ? (
        <span className="data-table__summary-chip">
          <UserCheck size={14} strokeWidth={2.25} aria-hidden />
          <span className="data-table__summary-chip-text">Filtro: “{term}”</span>
          {onClearSearch ? (
            <button
              type="button"
              className="data-table__summary-clear"
              onClick={onClearSearch}
              aria-label="Limpiar filtro de búsqueda"
              title="Limpiar filtro"
            >
              <X size={14} strokeWidth={2.5} aria-hidden />
            </button>
          ) : null}
        </span>
      ) : null}
    </>
  ) : null;

  return (
    <DataTable<Requester>
      className="requesters-data-table"
      columns={columns}
      data={data}
      getRowId={(row) => row.id ?? fullName(row)}
      loading={loading}
      emptyMessage={term ? 'Sin solicitantes para esta búsqueda' : 'Sin solicitantes'}
      emptyDescription={
        term
          ? 'Prueba con otro nombre o limpia el filtro.'
          : 'Crea un nuevo solicitante para comenzar.'
      }
      toolbar={toolbar}
      summary={summary}
      footer={footer}
      actionsHeader="Acciones"
      aria-label="Tabla de solicitantes"
      renderActions={(row) => (
        <>
          <button
            type="button"
            className="data-table__action data-table__action--edit"
            onClick={() => onEdit(row)}
            title="Editar solicitante"
            aria-label={`Editar ${fullName(row) || 'solicitante'}`}
          >
            <Pencil size={16} strokeWidth={2} aria-hidden />
            <span>Editar</span>
          </button>
          {row.id != null ? (
            <button
              type="button"
              className="data-table__action data-table__action--danger"
              onClick={() => onDelete(Number(row.id))}
              title="Eliminar solicitante"
              aria-label={`Eliminar ${fullName(row) || 'solicitante'}`}
            >
              <Trash2 size={16} strokeWidth={2} aria-hidden />
              <span>Eliminar</span>
            </button>
          ) : null}
        </>
      )}
    />
  );
};

export default RequestersTable;
